'use client';

import React, { useState } from 'react';
import { Calendar, Clock, AlertCircle, CheckCircle2, ChevronRight, Bell } from 'lucide-react';
import CounsellingModal from '@/components/CounsellingModal';

interface CalendarEvent {
  id: string;
  title: string;
  date: string;
  category: 'MCC' | 'State' | 'Registration' | 'Document' | 'Seat';
  description: string;
  status: 'upcoming' | 'active' | 'completed';
  important: boolean;
}

const events: CalendarEvent[] = [
  { id: 'ev-001', title: 'NEET UG 2026 Result Declaration', date: 'Jun 14, 2026', category: 'MCC', description: 'NEET UG 2026 results announced by NTA. Download scorecard from nta.ac.in', status: 'completed', important: true },
  { id: 'ev-002', title: 'MCC AIQ Registration Opens', date: 'Jun 20, 2026', category: 'Registration', description: 'Registration for AIQ counselling begins at mcc.nic.in. Keep documents ready.', status: 'active', important: true },
  { id: 'ev-003', title: 'AIQ Round 1 Choice Filling', date: 'Jun 25 – Jul 2, 2026', category: 'MCC', description: 'Fill college preferences for AIQ Round 1. Carefully order your choices.', status: 'active', important: true },
  { id: 'ev-004', title: 'AIQ Round 1 Seat Allotment', date: 'Jul 8, 2026', category: 'Seat', description: 'Round 1 seat allotment result published. Check your allotment at mcc.nic.in', status: 'upcoming', important: true },
  { id: 'ev-005', title: 'AIQ Round 1 Reporting', date: 'Jul 9–15, 2026', category: 'Document', description: 'Report to allotted college with original documents for verification.', status: 'upcoming', important: false },
  { id: 'ev-006', title: 'AIQ Round 2 Choice Filling', date: 'Jul 18–24, 2026', category: 'MCC', description: 'Round 2 choice filling for candidates who want to upgrade or new registrations.', status: 'upcoming', important: false },
  { id: 'ev-007', title: 'AIQ Round 2 Seat Allotment', date: 'Jul 28, 2026', category: 'Seat', description: 'Round 2 seat allotment result published.', status: 'upcoming', important: false },
  { id: 'ev-008', title: 'Maharashtra State Counselling Begins', date: 'Jul 5, 2026', category: 'State', description: 'Maharashtra CET Cell opens registration for state quota MBBS/BDS counselling.', status: 'upcoming', important: false },
  { id: 'ev-009', title: 'Karnataka State Counselling Begins', date: 'Jul 8, 2026', category: 'State', description: 'KEA opens registration for Karnataka state quota MBBS counselling.', status: 'upcoming', important: false },
  { id: 'ev-010', title: 'Tamil Nadu State Counselling Begins', date: 'Jul 10, 2026', category: 'State', description: 'Tamil Nadu Health Department opens state quota MBBS counselling registration.', status: 'upcoming', important: false },
  { id: 'ev-011', title: 'MCC Mop-Up Round', date: 'Aug 10–20, 2026', category: 'MCC', description: 'Mop-up round for unfilled AIQ seats. Last chance for AIQ admission.', status: 'upcoming', important: true },
  { id: 'ev-012', title: 'Stray Vacancy Round', date: 'Aug 25–30, 2026', category: 'MCC', description: 'Final stray vacancy round for remaining unfilled seats across all categories.', status: 'upcoming', important: false },
];

const categoryColors: Record<string, string> = {
  MCC: 'bg-primary/10 text-primary border-primary/20',
  State: 'bg-accent/10 text-accent border-accent/20',
  Registration: 'bg-success/10 text-success border-success/20',
  Document: 'bg-warning/10 text-warning border-warning/20',
  Seat: 'bg-danger/10 text-danger border-danger/20',
};

const statusConfig = {
  completed: { icon: CheckCircle2, color: 'text-success', bg: 'bg-success/10', label: 'Completed' },
  active: { icon: Clock, color: 'text-warning', bg: 'bg-warning/10', label: 'Active Now' },
  upcoming: { icon: Calendar, color: 'text-muted-foreground', bg: 'bg-slate-100', label: 'Upcoming' },
};

export default function AdmissionCalendarClient() {
  const [filter, setFilter] = useState<string>('All');
  const [showImportantOnly, setShowImportantOnly] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  const categories = ['All', 'MCC', 'State', 'Registration', 'Document', 'Seat'];

  const filtered = events.filter(e =>
    (filter === 'All' || e.category === filter) &&
    (!showImportantOnly || e.important)
  );

  return (
    <div className="pt-20 pb-16">
      {/* Header */}
      <div className="bg-gradient-to-br from-primary/8 via-transparent to-accent/6 border-b border-slate-100 py-12 px-6 lg:px-8">
        <div className="max-w-screen-xl mx-auto">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 glass-card rounded-full text-xs text-primary border border-primary/15 mb-4 font-semibold">
            <Calendar size={12} />
            <span>NEET 2026 Counselling Schedule</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-extrabold text-foreground mb-3 tracking-tight">
            Admission <span className="gradient-text">Calendar</span>
          </h1>
          <p className="text-muted-foreground text-lg max-w-2xl">
            Complete NEET 2026 counselling timeline — MCC AIQ rounds, state counselling, registration deadlines, and seat allotment dates.
          </p>
        </div>
      </div>

      <div className="max-w-screen-xl mx-auto px-6 lg:px-8 py-10">
        {/* Filters */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 mb-8">
          <div className="flex flex-wrap gap-2">
            {categories.map(cat => (
              <button
                key={cat}
                onClick={() => setFilter(cat)}
                className={`px-4 py-2 rounded-xl text-sm font-semibold border transition-all ${filter === cat ? 'bg-primary text-white border-primary' : 'glass-card text-muted-foreground border-white/50 hover:text-foreground'}`}
              >
                {cat}
              </button>
            ))}
          </div>
          <button
            onClick={() => setShowImportantOnly(!showImportantOnly)}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold border transition-all ml-auto ${showImportantOnly ? 'bg-warning text-white border-warning' : 'glass-card text-muted-foreground border-white/50'}`}
          >
            <AlertCircle size={14} />
            Important Only
          </button>
        </div>

        {/* Events */}
        <div className="space-y-4">
          {filtered.map(event => {
            const status = statusConfig[event.status];
            return (
              <div
                key={event.id}
                className={`glass-card-white rounded-2xl p-5 border border-white/60 flex flex-col sm:flex-row items-start gap-4 ${event.status === 'active' ? 'ring-2 ring-warning/20' : ''}`}
              >
                {/* Status icon */}
                <div className={`w-10 h-10 rounded-2xl ${status.bg} flex items-center justify-center flex-shrink-0`}>
                  <status.icon size={18} className={status.color} />
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <h3 className="text-sm font-bold text-foreground">{event.title}</h3>
                    {event.important && (
                      <span className="text-xs px-2 py-0.5 bg-danger/10 text-danger border border-danger/20 rounded-full font-semibold">Important</span>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mb-2">{event.description}</p>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className={`text-xs px-2.5 py-1 rounded-full border font-semibold ${categoryColors[event.category]}`}>
                      {event.category}
                    </span>
                    <span className="text-xs text-muted-foreground flex items-center gap-1">
                      <Calendar size={11} />
                      {event.date}
                    </span>
                  </div>
                </div>

                {/* Status badge */}
                <div className={`px-3 py-1.5 rounded-xl text-xs font-bold ${status.bg} ${status.color} flex-shrink-0`}>
                  {status.label}
                </div>
              </div>
            );
          })}
        </div>

        {/* CTA */}
        <div className="mt-10 glass-card-white rounded-3xl p-8 border border-white/60 text-center">
          <Bell size={32} className="text-primary mx-auto mb-4" />
          <h2 className="text-xl font-extrabold text-foreground mb-2">Never Miss a Deadline</h2>
          <p className="text-muted-foreground text-sm mb-6 max-w-md mx-auto">
            Book a counselling session with our experts to get personalized guidance on each counselling round.
          </p>
          <button onClick={() => setModalOpen(true)} className="btn-primary inline-flex items-center gap-2 px-6 py-3">
            Book Free Counselling
            <ChevronRight size={16} />
          </button>
        </div>
      </div>
      <CounsellingModal isOpen={modalOpen} onClose={() => setModalOpen(false)} source="Admission Calendar" />
    </div>
  );
}
